// Shim that adds React 18's `useId` and `useInsertionEffect` on top of React 17,
// so libraries that require them (framer-motion) can run unchanged. Wired up via
// `resolve.alias` in vite.config.ts — every bare `import ... from 'react'` resolves
// here instead of the real package.
//
// The subpath `react/index.js` is used to bypass the alias and reach the real React
// package; a bare `from 'react'` here would loop back into this file.
//
// Re-exports are enumerated explicitly because `react/index.js` is CJS — esbuild
// can't statically detect its named exports, so `export *` would drop them all.
import * as React from 'react/index.js';

export default React;

export const {
    Children,
    Component,
    Fragment,
    Profiler,
    PureComponent,
    StrictMode,
    Suspense,
    cloneElement,
    createContext,
    createElement,
    createFactory,
    createRef,
    forwardRef,
    isValidElement,
    lazy,
    memo,
    useCallback,
    useContext,
    useDebugValue,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
    version,
    // Required by react/jsx-dev-runtime — it reaches into this object for
    // ReactCurrentDispatcher. Without it, every JSX render crashes at startup.
    __SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED,
} = React;

// Counter-based polyfill — React 17 has no useId. Stable across re-renders via the ref;
// not SSR-safe, but this app is client-only.
let counter = 0;

export const useId = (): string => {
    const ref = React.useRef<string | null>(null);
    if (ref.current === null) {
        ref.current = `:r${(counter++).toString(36)}:`;
    }
    return ref.current;
};

// useInsertionEffect doesn't exist in React 17. useLayoutEffect is the conventional
// fallback (same one styled-components / emotion use); fires slightly later than the
// real thing but is fine for framer-motion's style-tag injection.
export const useInsertionEffect = React.useLayoutEffect;
